/**
 * S1b-c 멤버 일상 글 상세 · 답글 (시안 05, 결정 24, D47).
 *
 *   상단 바(뒤로 · 「일상 글」) → 멤버 글(표식 40 + 이름 / 소개 · 시간 + 「일상」 칩 → 마루 부리 17/29)
 *   → 답글(멤버끼리 답글은 미리 만든 장면) → 입력창 「{멤버}의 글에 답글 남기기」
 *
 * - **내가 이 글에 답글을 달면 글쓴이 멤버가 반드시 대댓글**(1.2~2.5초), 이름 옆 「글쓴이」 표시
 * - 다른 멤버 답글에 대댓글을 달면 그 답글 쓴 멤버가 답하고 글쓴이는 끼어들지 않음
 * - 내 답글 줄은 나만 봅니다. 1:1로 이어 가면 그 멤버 1:1 맨 위에 인용 상자
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMember } from '@config/members/index.js';
import { TopBar } from '../../components/TopBar.jsx';
import { Avatar } from '../../components/Avatar.jsx';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import { ReplyLine, ThreadComposer, ThreadPost } from '../../components/thread/Thread.jsx';
import { errorMessageOf, isAbortError } from '../../api/client.js';
import * as boardApi from '../../api/board.js';
import * as chatApi from '../../api/chat.js';
import { useGoBack } from '../../hooks/useGoBack.js';
import { useSession } from '../../stores/session.js';
import { formatClock } from './boardFeed.js';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60 * 1000;

export function MemberPostPage() {
    const { memberPostId } = useParams();
    const navigate = useNavigate();
    const goBack = useGoBack('/board');
    const displayName = useSession((state) => state.user?.displayName) ?? '단골';
    const [thread, setThread] = useState(null);
    const [error, setError] = useState(null);
    const [target, setTarget] = useState(null);
    const [safetyCollapsed, setSafetyCollapsed] = useState(false);
    const pollStartedAtRef = useRef(Date.now());

    const load = useCallback(
        async (signal) => {
            try {
                setThread(await boardApi.fetchMemberThread(memberPostId, { signal }));
                setError(null);
            } catch (requestError) {
                if (!isAbortError(requestError)) {
                    setError(errorMessageOf(requestError, '글을 불러오지 못했어요.'));
                }
            }
        },
        [memberPostId]
    );

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    const waiting = Boolean(thread?.pending);
    useEffect(() => {
        if (!waiting || Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
            return undefined;
        }
        const timer = setTimeout(() => load(), POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [waiting, thread, load]);

    async function send(text) {
        setError(null);
        try {
            const data = await boardApi.postMemberReply(memberPostId, { text, parentId: target?.repliesId ?? null });
            setThread(data);
            setTarget(null);
            pollStartedAtRef.current = Date.now();
        } catch (requestError) {
            setError(errorMessageOf(requestError, '답글을 보내지 못했어요.'));
            throw requestError;
        }
    }

    async function continueInChat(reply) {
        setError(null);
        try {
            const { memberKey, origin } = await chatApi.openChatFromReply(reply.memberKey, {
                memberPostId: Number(memberPostId),
                replyId: reply.repliesId,
            });
            navigate(`/chat/${memberKey}`, { state: { from: 'reply', fromReply: { ...origin, memberKey } } });
        } catch (requestError) {
            setError(errorMessageOf(requestError, '1:1을 열지 못했어요.'));
        }
    }

    const post = thread?.post;
    const member = post ? getMember(post.memberKey) : null;
    return (
        <div className="flex h-dvh flex-col">
            <TopBar title="일상 글" onBack={goBack} />
            <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {post ? (
                    <>
                        <ThreadPost
                            mark={<Avatar memberKey={post.memberKey} size={40} />}
                            name={member.name}
                            meta={`${member.role} · ${formatClock(post.createdAt)}`}
                            kind="day"
                            body={post.body}
                            replyCount={thread.replyCount}
                            memberCount={thread.memberCount}
                            serif
                        />
                        {thread.crisis ? (
                            <div className="px-4 pt-3">
                                <SafetyCard
                                    collapsed={safetyCollapsed}
                                    onCollapse={() => setSafetyCollapsed(true)}
                                    onExpand={() => setSafetyCollapsed(false)}
                                />
                            </div>
                        ) : null}
                        {thread.lines.map((line) => (
                            <ReplyLine
                                key={line.repliesId}
                                line={line}
                                displayName={displayName}
                                authorKey={post.memberKey}
                                activeTargetId={target?.repliesId ?? null}
                                pending={thread.pending}
                                onReply={(reply) => setTarget(target?.repliesId === reply.repliesId ? null : reply)}
                                onChat={continueInChat}
                            />
                        ))}
                        {thread.systemLines.map((line) => (
                            <p
                                key={line.repliesId}
                                className="m-0 px-4 py-3 text-center text-[12px] text-text-secondary"
                            >
                                {line.body}
                            </p>
                        ))}
                    </>
                ) : null}
                {!post && !error ? <p className="m-0 p-5 text-text-secondary">불러오는 중이에요.</p> : null}
                {error ? (
                    <p role="alert" className="m-0 px-4 py-2 text-caption text-error">
                        {error}
                    </p>
                ) : null}
            </main>
            {post ? (
                <ThreadComposer
                    target={target}
                    placeholder={`${member.name}의 글에 답글 남기기`}
                    disabled={Boolean(thread.pending)}
                    onClear={() => setTarget(null)}
                    onSend={send}
                />
            ) : null}
        </div>
    );
}
